// ── Order BOM at posting time ───────────────────────────────────────────────

import { supabase } from '@/lib/supabase';

/** One line of an order's component list, at order scale (qty = per-build × units). */
export type BomRow = { sku: string; type: string; qty: number; cost: number };

type ReservationLine = {
  component_sku: string;
  component_type: string | null;
  quantity: number | null;
  unit_cost: number | null;
};

type CopyLine = {
  component_sku: string;
  component_type: string | null;
  component_qty: number | null;
  cost_unit_ex_vat: number | null;
};

/** reservation_components.quantity is per-build; scale it to the order's units. */
export function bomFromReservation(lines: ReservationLine[], aoQty: number): BomRow[] {
  return lines.map(c => ({
    sku:  c.component_sku,
    type: c.component_type || '',
    qty:  (c.quantity || 1) * aoQty,
    cost: c.unit_cost ?? 0,
  }));
}

export function bomFromCopy(lines: CopyLine[]): BomRow[] {
  return lines.map(c => ({
    sku:  c.component_sku,
    type: c.component_type || '',
    qty:  c.component_qty || 1,
    cost: c.cost_unit_ex_vat ?? 0,
  }));
}

// Quantities and costs are compared at the precision the columns store, so
// 1.0000 from the reservation equals 1.00 on the order.
const lineKey = (r: BomRow) =>
  `${r.sku.trim().toLowerCase()}|${r.qty.toFixed(2)}|${r.cost.toFixed(2)}`;

/**
 * Lines on one side but not the other, compared as multisets — a reservation
 * can legitimately carry the same SKU twice (two identical SSDs on separate
 * lines), so a set comparison would miss a dropped duplicate.
 */
export function bomDiff(copy: BomRow[], expected: BomRow[]): { removed: BomRow[]; added: BomRow[] } {
  const remaining = new Map<string, BomRow[]>();
  for (const r of expected) {
    const k = lineKey(r);
    remaining.set(k, [...(remaining.get(k) ?? []), r]);
  }
  const removed: BomRow[] = [];
  for (const r of copy) {
    const bucket = remaining.get(lineKey(r));
    if (bucket?.length) bucket.pop();
    else removed.push(r);
  }
  const added = [...remaining.values()].flat();
  return { removed, added };
}

export type PostingBom = {
  bom: BomRow[];
  /** How the order's copy got to match: already did, written fresh, or replaced. */
  source: 'existing' | 'created' | 'refreshed';
  diff: { removed: BomRow[]; added: BomRow[] } | null;
};

/**
 * The component list an order is posted with, made to agree with its
 * reservation first.
 *
 * The order keeps its own copy (assembly_order_components), but editing a
 * reservation only refreshes the copies of orders still pending — orders
 * already in progress are left alone so the technician's sheet does not change
 * under them. Posting from that stale copy deducted parts that were never used:
 * ASO-0000074 posted a case that had been swapped out on its reservation five
 * days earlier. The reservation is where swaps are recorded, so when the two
 * disagree the reservation wins and the copy is rewritten to match.
 *
 * Orders without a reservation (legacy) post from their copy unchanged.
 */
export async function resolvePostingBom(
  aoId: string,
  reservationId: string | null | undefined,
  aoQty: number,
): Promise<PostingBom> {
  const { data: existingRows, error: exErr } = await supabase
    .from('assembly_order_components')
    .select('component_sku, component_type, component_qty, cost_unit_ex_vat')
    .eq('ao_id', aoId);
  if (exErr) throw exErr;
  const existing = bomFromCopy(existingRows ?? []);

  if (!reservationId) {
    if (existing.length) return { bom: existing, source: 'existing', diff: null };
    throw new Error('Cannot post: this assembly order has no linked reservation. Component data is missing.');
  }

  const { data: resComps, error: resErr } = await supabase
    .from('reservation_components')
    .select('component_sku, component_type, quantity, unit_cost')
    .eq('reservation_id', reservationId);
  if (resErr) throw resErr;
  if (!resComps?.length) {
    if (existing.length) return { bom: existing, source: 'existing', diff: null };
    throw new Error('Cannot post: no component data found in the linked reservation. Verify reservation integrity before posting.');
  }

  const expected = bomFromReservation(resComps, aoQty);
  let source: PostingBom['source'] = 'created';
  let diff: PostingBom['diff'] = null;

  if (existing.length) {
    diff = bomDiff(existing, expected);
    if (!diff.removed.length && !diff.added.length) {
      return { bom: existing, source: 'existing', diff: null };
    }
    source = 'refreshed';
    const { error: delErr } = await supabase.from('assembly_order_components').delete().eq('ao_id', aoId);
    if (delErr) throw delErr;
  }

  const { error: insErr } = await supabase.from('assembly_order_components').insert(
    expected.map(c => ({
      ao_id:             aoId,
      component_sku:     c.sku,
      component_type:    c.type,
      component_qty:     c.qty,
      cost_unit_ex_vat:  c.cost,
      cost_batch_ex_vat: c.cost * c.qty,
    }))
  );
  if (insErr) throw insErr;

  return { bom: expected, source, diff };
}

type AllocatedLine = { id: string; component_sku: string; allocated_qty: number | null };

/**
 * New allocated_qty for each reservation line a posted order draws on.
 *
 * What the order used is totalled per SKU and taken from that SKU's lines in
 * turn. Matching each order line to the first reservation line with its SKU
 * instead meant a SKU reserved on two lines (two identical SSDs) had both
 * deductions land on the first, leaving the second allocated after posting.
 * Only lines whose allocation changes are returned.
 */
export function allocationDeductions(
  lines: AllocatedLine[],
  bom: BomRow[],
): { id: string; allocated_qty: number }[] {
  const norm = (s: string) => s.trim().toLowerCase();
  const need = new Map<string, number>();
  for (const b of bom) need.set(norm(b.sku), (need.get(norm(b.sku)) ?? 0) + b.qty);

  const updates: { id: string; allocated_qty: number }[] = [];
  for (const line of lines) {
    const key = norm(line.component_sku);
    const remaining = need.get(key) ?? 0;
    const allocated = line.allocated_qty ?? 0;
    if (remaining <= 0 || allocated <= 0) continue;
    const take = Math.min(remaining, allocated);
    need.set(key, remaining - take);
    updates.push({ id: line.id, allocated_qty: allocated - take });
  }
  return updates;
}
